import React, { useState, useRef, FormEvent } from 'react';
import styled from 'styled-components';

import Icon from '../../../../components/Icon/Icon';
import Bubble from '../../../../components/Bubble/Bubble';
import Button from '../../../../components/Button/Button';
import Box from '../../../../components/Box/Box';
import HStack from '../../../../components/HStack/HStack';
import Divider from '../../../../components/Divider/Divider';
import Field from '../../../../components/Field/Field';
import Form from '../../../../components/Form/Form';
import CustomText from '../../../../components/Typography/CustomText/CustomText';

import SliderErrorsInputSwitch from './SliderErrorsInputSwitch';
import { OnSliderErrorsSubmitType, SpreadsheetCallbackType } from '../../types';
import { EnhancedErrorType } from './SliderErrors';

const SliderErrorsWrapper = styled.li`
  list-style-type: none;
  width: 100%;
`;

export type ErrorStateType = {
  open: boolean;
  value?: string;
};

interface SliderErrorsDetailsProps {
  index: number;
  error: EnhancedErrorType;
  errorState: ErrorStateType;
  getDataAtCell: SpreadsheetCallbackType['getDataAtCell'];
  setDataAtCell: SpreadsheetCallbackType['setDataAtCell'];
  onSliderErrorsSubmit?: OnSliderErrorsSubmitType;
  onToggleCallback: (errorId: string, value?: string) => void;
}

const SliderErrorsDetails = (props: SliderErrorsDetailsProps) => {
  const { index, error, errorState, getDataAtCell, setDataAtCell, onSliderErrorsSubmit, onToggleCallback } = props;

  const [openTwiddle, setOpenTwiddle] = useState(errorState.open || index === 0);
  const [inputValue, setInputValue] = useState<string>(errorState.value || '');

  /*
    This ref is being used as a way to control when the component rerenders,
    which should happen only when the cellValue.current is set (or is !== null).
  */
  const cellValue = useRef('');

  cellValue.current = getDataAtCell(error.row, error.col) || '';

  const handleInputSubmit = (e: MouseEvent | FormEvent) => {
    e.preventDefault();
    setDataAtCell(error.row, error.col, inputValue);
    if (onSliderErrorsSubmit) {
      onSliderErrorsSubmit({
        ...error,
        value: inputValue,
      });
    }
  };

  const inputId = `${error.col}-${error.row}-error-input`;

  return (
    <SliderErrorsWrapper>
      <Box vertical="16" as="div">
        <HStack
          alignY="center"
          spacing="8"
          onClick={e => {
            onToggleCallback(`${error.col}-${error.row}`);
            setOpenTwiddle(!openTwiddle);
          }}
        >
          <Bubble variant="feedback-negative">{error.humanCoords}</Bubble>
          <Icon type={openTwiddle ? 'down-bold' : 'forward-bold'} placement="low" />
          <CustomText weight="500">
            {error.colHeader} {cellValue.current ? 'has errors' : 'is required'}
          </CustomText>
        </HStack>
        <Form id={`${inputId}-form`} onSubmit={handleInputSubmit} allowEnterKeySubmit>
          {openTwiddle && (
            <Box horizontal="16px">
              <Field
                htmlFor={inputId}
                label={error.colHeader}
                error={cellValue.current ? `The value in the cell is "${cellValue.current}"` : 'The cell is empty'}
                help={error.externalErrorMessage || error.errorMessage}
                isStacked
                isFull
              >
                <SliderErrorsInputSwitch
                  inputType={error.colType}
                  colSourceOptions={error.colSourceOptions}
                  inputId={inputId}
                  inputValue={inputValue}
                  errorId={`${error.col}-${error.row}`}
                  setInputValue={setInputValue}
                  onValueInput={onToggleCallback}
                />
              </Field>

              <Button type="primary" size="small" htmlType="submit" onClick={handleInputSubmit}>
                Submit
              </Button>
            </Box>
          )}
        </Form>
      </Box>
      <Divider weight="strong" />
    </SliderErrorsWrapper>
  );
};

export default SliderErrorsDetails;
